import { memo, useEffect, useState } from 'react';
import { AlertTriangle, BookOpen, FileText, Loader2, RefreshCw, Database, FileSearch, Download, Sparkles, ListChecks } from 'lucide-react';
import Button from '../../ui/Button';
import { DocumentInsightsPanel } from './DocumentInsightsPanel';
import LoadingCard from '../../ui/LoadingCard';
import SmartLoader from '../../ui/SmartLoader';
import type { SmartLoaderPhase } from '../../ui/SmartLoader';
import { StatusBanner } from './StatusBanner';
import { SummaryHero } from './SummaryHero';
import { useDocumentInsights } from './useDocumentInsights';

interface DocumentInsightsTabProps {
  companyName: string;
  country: string;
}

type DocTab = 'mda' | 'earnings';

const DOC_TABS: { id: DocTab; label: string }[] = [
  { id: 'mda', label: 'MDA' },
  { id: 'earnings', label: 'Earnings' },
];

// Cosmetic time-based phases (the request is a single opaque POST). startSec are
// the cumulative durations of the underlying pipeline (~46s cold on first run).
const DOC_INSIGHTS_PHASES: SmartLoaderPhase[] = [
  { id: 'cache', label: 'Checking cached insights', hint: 'Reusing prior analysis when available', icon: Database, startSec: 0 },
  { id: 'filings', label: 'Fetching latest filings', hint: 'MDA and earnings documents', icon: FileSearch, startSec: 3 },
  { id: 'download', label: 'Downloading documents', hint: 'Retrieving source files', icon: Download, startSec: 8 },
  { id: 'extract', label: 'Running AI extraction', hint: 'Parsing management commentary', icon: Sparkles, startSec: 16 },
  { id: 'insights', label: 'Generating executive insights', hint: 'Summary, sentiment, highlights', icon: FileText, startSec: 31 },
  { id: 'finalize', label: 'Finalizing analysis', hint: 'Assembling the report', icon: ListChecks, startSec: 41 },
];
const DOC_INSIGHTS_EXPECTED_SEC = 46;

function DocumentInsightsTabBase({ companyName, country }: DocumentInsightsTabProps) {
  const {
    data,
    isLoading,
    isRefreshing,
    error,
    fetchedAt,
    fetch,
    refresh,
  } = useDocumentInsights({ companyName, country });

  const [docTab, setDocTab] = useState<DocTab>('mda');

  const mdaDoc = data?.mda;
  const earningsDoc = data?.earnings;
  const mdaOk = mdaDoc?.status === 'success';
  const earningsOk = earningsDoc?.status === 'success';

  // Auto-switch to the available tab when one fails
  useEffect(() => {
    if (!data) return;
    if (docTab === 'mda' && !mdaOk && earningsOk) setDocTab('earnings');
    if (docTab === 'earnings' && !earningsOk && mdaOk) setDocTab('mda');
  }, [data, mdaOk, earningsOk]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Sticky header shared across all states ────────────────────────────────

  const StickyHeader = (
    <div className="sticky top-0 z-30 -mx-6 border-b border-neutral-100 bg-white/95 px-6 py-3 backdrop-blur supports-[backdrop-filter]:bg-white/80">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2.5 min-w-0">
          <div className="w-6 h-6 rounded-md bg-primary-50 flex items-center justify-center shrink-0">
            <BookOpen className="h-3.5 w-3.5 text-primary-600" aria-hidden="true" />
          </div>
          <h2 className="text-base font-bold text-neutral-900 truncate">{companyName}</h2>
          <span className="hidden sm:block text-[10px] font-bold uppercase tracking-widest text-secondary-600 shrink-0">
          </span>
        </div>

        {data && (
          <Button
            variant="outline"
            size="sm"
            onClick={refresh}
            disabled={isRefreshing}
            aria-label="Refresh document analysis"
          >
            {isRefreshing ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            ) : (
              <RefreshCw className="h-4 w-4" aria-hidden="true" />
            )}
            Refresh
          </Button>
        )}
      </div>
    </div>
  );

  // ─── Loading (cold — no data yet) ─────────────────────────────────────────

  if (isLoading) {
    return (
      <div>
        {StickyHeader}
        <div className="pt-6">
          <SmartLoader
            title="Extracting document insights"
            subtitle="AI extraction · up to 50s on first run"
            phases={DOC_INSIGHTS_PHASES}
            progress={{ mode: 'elapsed', expectedSec: DOC_INSIGHTS_EXPECTED_SEC }}
            active={isLoading}
          />
        </div>
      </div>
    );
  }

  // ─── Error (no data at all) ────────────────────────────────────────────────

  if (error && !data) {
    return (
      <div>
        {StickyHeader}
        <div className="py-14 flex flex-col items-center gap-4 text-center">
          <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center">
            <AlertTriangle className="h-5 w-5 text-red-500" aria-hidden="true" />
          </div>
          <div>
            <p className="text-sm font-semibold text-red-700">{error}</p>
            <p className="text-xs text-neutral-400 mt-1">
              Unable to retrieve document insights.
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={fetch}>
            <RefreshCw className="h-4 w-4" aria-hidden="true" />
            Retry
          </Button>
        </div>
      </div>
    );
  }

  // ─── Not yet fetched — initial prompt ─────────────────────────────────────

  if (!data) {
    return (
      <div>
        {StickyHeader}
        <div className="py-14 flex flex-col items-center gap-4 text-center">
          <div className="w-12 h-12 rounded-full bg-primary-50 flex items-center justify-center">
            <BookOpen className="h-6 w-6 text-primary-500" aria-hidden="true" />
          </div>
          <div>
            <p className="text-sm font-semibold text-neutral-800">Run Document Analysis</p>
            <p className="text-xs text-neutral-400 mt-1 max-w-xs">
              AI-powered extraction of insights from MDA and Earnings documents.
            </p>
          </div>
          <Button variant="primary" size="sm" onClick={fetch}>
            Start Analysis
          </Button>
        </div>
      </div>
    );
  }

  // ─── Success / Partial / Failed with data ─────────────────────────────────

  const activeDoc = docTab === 'mda' ? mdaDoc : earningsDoc;

  return (
    <div>
      {StickyHeader}

      <div className="pt-5 space-y-5">
        {/* Overall status banner */}
        <StatusBanner
          status={data.status}
          mdaStatus={mdaDoc?.status}
          earningsStatus={earningsDoc?.status}
          onRetry={refresh}
        />

        {/* Hero: company name + combined summary + refresh */}
        <SummaryHero
          companyName={data.company_name}
          combinedSummary={data.combined_summary}
          status={data.status}
          fetchedAt={fetchedAt}
          isRefreshing={isRefreshing}
          onRefresh={refresh}
        />

        {/* Document sub-tabs */}
        <div
          className="border-b border-neutral-200"
          role="tablist"
          aria-label="Document type selection"
        >
          <nav className="flex gap-1">
            {DOC_TABS.map(({ id, label }) => {
              const docResult = id === 'mda' ? mdaDoc : earningsDoc;
              const ok = docResult?.status === 'success';
              const isActive = docTab === id;
              return (
                <button
                  key={id}
                  role="tab"
                  aria-selected={isActive}
                  aria-controls={`docpanel-${id}`}
                  id={`doctab-${id}`}
                  onClick={() => setDocTab(id)}
                  className={`flex items-center gap-2 px-4 py-3 border-b-2 text-sm font-medium transition-colors ${
                    isActive
                      ? 'border-primary-600 text-primary-700'
                      : 'border-transparent text-neutral-500 hover:text-neutral-800 hover:border-neutral-300'
                  } ${!ok && docResult ? 'opacity-60' : ''}`}
                >
                  <FileText
                    className={`h-4 w-4 ${isActive ? 'text-primary-600' : 'text-neutral-400'}`}
                    aria-hidden="true"
                  />
                  {label}
                  {!ok && docResult && (
                    <AlertTriangle
                      className="h-3.5 w-3.5 text-amber-500"
                      aria-label="Document unavailable"
                    />
                  )}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Document panel content */}
        <div
          role="tabpanel"
          id={`docpanel-${docTab}`}
          aria-labelledby={`doctab-${docTab}`}
        >
          {isRefreshing ? (
            <LoadingCard message="Refreshing document insights…" delayMs={0} />
          ) : activeDoc ? (
            <DocumentInsightsPanel doc={activeDoc} />
          ) : (
            <div className="py-10 text-center text-xs text-neutral-400">
              No document data available for this type.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export const DocumentInsightsTab = memo(DocumentInsightsTabBase);
